import { supabase } from "./supabase";

/**
 * Append a row to `public.admin_audit_log` via the SECURITY DEFINER
 * `log_admin_action` RPC. The RPC enforces that the caller is admin
 * and snapshots email + role at write time.
 *
 * Failures are non-blocking: the calling write path has already
 * succeeded by the time we audit, and refusing to audit shouldn't
 * undo the work. We `console.warn` and return null.
 *
 * Usage:
 *   await logAdminAction({
 *     action: "role.set",
 *     entityType: "user_profiles",
 *     entityId: userId,
 *     summary: `Set role to ${role} for ${email}`,
 *     diff: { before: { role: prev }, after: { role } },
 *   });
 */

export type AuditDiff = {
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
};

export type AuditInput = {
  action: string;
  entityType: string;
  entityId?: string | null;
  summary: string;
  diff?: AuditDiff | null;
};

export async function logAdminAction(input: AuditInput): Promise<string | null> {
  const { data, error } = await supabase.rpc("log_admin_action", {
    p_action: input.action,
    p_entity_type: input.entityType,
    p_entity_id: input.entityId ?? null,
    p_summary: input.summary,
    p_diff: input.diff ?? null,
  });
  if (error) {
    console.warn("audit log write failed", error);
    return null;
  }
  return typeof data === "string" ? data : null;
}

/* ───── Read-side: paginated list for the audit log page ───── */

export type AdminAuditLogRow = {
  id: string;
  actor_id: string | null;
  actor_email: string | null;
  actor_role: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  summary: string;
  diff: AuditDiff | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
};

export type AuditCategory =
  | "all"
  | "roles"
  | "team"
  | "competitions"
  | "positions"
  | "winners"
  | "disbursements"
  | "offers"
  | "inquiries"
  | "announcements"
  | "invites"
  | "domains"
  | "exports"
  | "impersonate"
  | "other";

export function categoryForAction(action: string): AuditCategory {
  if (action.startsWith("role.")) return "roles";
  if (
    action.startsWith("team.") ||
    action.startsWith("campus_admin.") ||
    action.startsWith("manager.") ||
    action.startsWith("judge_assignment.")
  ) {
    return "team";
  }
  if (action.startsWith("competition.") || action.startsWith("deliverable.") || action === "rubric.weights_set") {
    return "competitions";
  }
  if (action.startsWith("position.")) return "positions";
  if (action.startsWith("winner_selection.")) return "winners";
  if (action.startsWith("disbursement.")) return "disbursements";
  if (action.startsWith("interview_offer.")) return "offers";
  if (action.startsWith("inquiry.")) return "inquiries";
  if (action.startsWith("announcement.")) return "announcements";
  if (action.startsWith("invite.")) return "invites";
  if (action.startsWith("domain.")) return "domains";
  if (action.startsWith("export.")) return "exports";
  if (action.startsWith("impersonate.")) return "impersonate";
  return "other";
}

export type ListAuditLogFilter = {
  search?: string;
  fromIso?: string | null;
  category?: AuditCategory;
  actorId?: string | null;
  entityType?: string | null;
  limit?: number;
  offset?: number;
};

export async function listAdminAuditLog(filter: ListAuditLogFilter = {}): Promise<AdminAuditLogRow[]> {
  let q = supabase
    .from("admin_audit_log")
    .select(
      "id, actor_id, actor_email, actor_role, action, entity_type, entity_id, summary, diff, ip, user_agent, created_at"
    )
    .order("created_at", { ascending: false });

  if (filter.fromIso) q = q.gte("created_at", filter.fromIso);
  if (filter.actorId) q = q.eq("actor_id", filter.actorId);
  if (filter.entityType && filter.entityType !== "any") q = q.eq("entity_type", filter.entityType);

  // Category filter via prefix on `action`. Some categories own multiple
  // action prefixes (team includes manager/campus_admin/judge_assignment;
  // competitions includes deliverable + rubric). Those use a multi-prefix
  // OR clause; single-prefix categories use a plain `.like`.
  if (filter.category && filter.category !== "all" && filter.category !== "other") {
    const prefixMap: Record<Exclude<AuditCategory, "all" | "other">, string | string[]> = {
      roles: "role.",
      team: ["team.", "campus_admin.", "manager.", "judge_assignment."],
      competitions: ["competition.", "deliverable.", "rubric."],
      positions: "position.",
      winners: "winner_selection.",
      disbursements: "disbursement.",
      offers: "interview_offer.",
      inquiries: "inquiry.",
      announcements: "announcement.",
      invites: "invite.",
      domains: "domain.",
      exports: "export.",
      impersonate: "impersonate.",
    };
    const prefix = prefixMap[filter.category];
    if (Array.isArray(prefix)) {
      const orClause = prefix.map((p) => `action.like.${p}%`).join(",");
      q = q.or(orClause);
    } else if (prefix) {
      q = q.like("action", `${prefix}%`);
    }
  }

  if (filter.search?.trim()) {
    const s = filter.search.trim();
    q = q.or(`actor_email.ilike.%${s}%,summary.ilike.%${s}%`);
  }

  const limit = filter.limit ?? 50;
  const offset = filter.offset ?? 0;
  q = q.range(offset, offset + limit - 1);

  const { data, error } = await q;
  if (error || !data) {
    if (error) console.warn("listAdminAuditLog failed", error);
    return [];
  }
  return data as AdminAuditLogRow[];
}

export async function listAuditActors(): Promise<{ actor_id: string; email: string }[]> {
  const { data } = await supabase
    .from("admin_audit_log")
    .select("actor_id, actor_email")
    .not("actor_id", "is", null)
    .not("actor_email", "is", null);
  const seen = new Map<string, string>();
  for (const r of (data ?? []) as { actor_id: string; actor_email: string }[]) {
    if (!seen.has(r.actor_id)) seen.set(r.actor_id, r.actor_email);
  }
  return [...seen.entries()].map(([actor_id, email]) => ({ actor_id, email }));
}

export async function listAuditEntityTypes(): Promise<string[]> {
  const { data } = await supabase.from("admin_audit_log").select("entity_type");
  const set = new Set<string>();
  for (const r of (data ?? []) as { entity_type: string }[]) set.add(r.entity_type);
  return [...set].sort();
}
